import {
  Block,
  Button,
  CloseIcon,
  ConfirmModal,
  FlexContainer,
  InputSelect,
  InputText,
  Modal,
  SoundNotificationContext,
  Span,
  useNotifications,
} from '@uni-space/uni-components';
import { useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useTranslation } from 'react-i18next';
import { cancelParcel } from '../api';
import { useCancellationReasons } from '../model/useCancellationReasons';

export interface CancelParcelState {
  trackNumber: {
    value: string;
    error: boolean;
  };
  reason: {
    value: string;
    error: boolean;
  };
}

const initState: CancelParcelState = {
  trackNumber: {
    value: '',
    error: false,
  },
  reason: {
    value: '',
    error: false,
  },
};

export default function CancelParcel(props: { onRefetch: () => Promise<void>; onClose: () => void; isOpen: boolean }) {
  const { onClose, isOpen, onRefetch } = props;
  const [isOpenAcceptance, setIsOpenAcceptance] = useState<boolean>(false);
  const [state, setState] = useState<CancelParcelState>(initState);
  const { addNotification } = useNotifications();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { t, i18n } = useTranslation();

  const handleChangeState = (key: keyof CancelParcelState, value: string) => {
    setState((prev) => ({ ...prev, [key]: { value, error: '' } }));
  };

  const handleOpenAcceptance = () => {
    let isError = false;

    for (const [key, objValue] of Object.entries(state)) {
      if (!objValue.value) {
        isError = true;
        setState((prev) => ({ ...prev, [key]: { value: objValue.value, error: true } }));
      }
    }

    if (isError) return;
    setIsOpenAcceptance(true);
  };

  const handleCloseAcceptance = () => {
    setIsOpenAcceptance(false);
  };

  const statusCancels = useCancellationReasons();

  const handleCloseCancelParcelModal = () => {
    onClose();
    setState(initState);
  };

  const { mutateAsync: fetchCancelParcel, isPending } = useMutation({
    mutationKey: ['cancel_parcel'],
    mutationFn: () => {
      const { label, reasonId } = statusCancels.find((item) => item.label === state.reason.value)!;
      return cancelParcel(state.trackNumber.value, reasonId, label);
    },
    onSuccess: () => {
      addNotification({ type: 'success', message: t('cancellationCompleted') });
      onSoundSuccess();
      onRefetch();
      handleCloseAcceptance();
      handleCloseCancelParcelModal();
    },
    onError: () => {
      onSoundError();
      handleCloseAcceptance();
      addNotification({ type: 'error', message: t('cancellationError') });
    },
  });

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseCancelParcelModal}>
        <FlexContainer style={{ position: 'relative' }} direction="column" gap="16px">
          <Block textAlign="center">
            <Span fs={18}>{t('cancelParcelButton')}</Span>
          </Block>
          <Block>
            <InputText
              id="track_number"
              value={state.trackNumber.value}
              onChange={(val) => handleChangeState('trackNumber', val)}
              label={t('trackNumberLabel')}
            />
            <Block pt={8}>
              <Span fs={12} color={state.trackNumber.error ? 'red' : 'rgba(0, 0, 0, 0.6)'}>
                {t('enterTrackingNumber')}
              </Span>
            </Block>
          </Block>
          <Block>
            <InputSelect
              withOutSearch
              value={state.reason.value}
              onChange={(val) => handleChangeState('reason', val)}
              id="reason"
              variant="primary"
              placeholder={t('reasonForReturn')}
              items={statusCancels}
            />
            <Block pt={8}>
              <Span fs={12} color={state.reason.error ? 'red' : 'rgba(0, 0, 0, 0.6)'}>
                {t('selectReasonForCancellation')}
              </Span>
            </Block>
          </Block>
          <FlexContainer justify="space-between">
            <Button onClick={handleCloseCancelParcelModal} width="100px" size="s">
              {t('close')}
            </Button>
            <Button
              disabled={Object.values(state).some((item) => item.error)}
              onClick={handleOpenAcceptance}
              type="error"
              width="100px"
              size="s"
            >
              {t('cancel')}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </Modal>
      <ConfirmModal
        onClose={handleCloseAcceptance}
        isOpen={isOpenAcceptance}
        isLoading={isPending}
        onSubmit={fetchCancelParcel}
        desc={t('confirmCancelParcel')}
        lang={i18n.language}
      />
    </>
  );
}
